import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { WompiReportsService, DailyTaskStatusDTO } from '../../services/wompi-reports.service';
import { AuthorizationService } from '../../services/authorization.service';
import { interval, Subscription } from 'rxjs';

@Component({
  selector: 'app-daily-task-banner',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <div *ngIf="showBanner && dailyTaskStatus && !dailyTaskStatus.isComplete" 
         class="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 shadow-lg">
      <div class="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-2">
        <div class="flex items-center gap-3">
          <svg class="w-5 h-5 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span class="font-medium">
            ¡Recordatorio! Debes cargar los reportes diarios de Wompi y Plink
          </span>
          <span class="text-amber-100 text-sm hidden md:inline">
            ({{ getStatusText() }})
          </span>
        </div>
        <div class="flex items-center gap-2">
          <a routerLink="/report-upload"
             class="bg-white/20 hover:bg-white/30 backdrop-blur-sm px-4 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Cargar Ahora
          </a>
          <button (click)="dismissBanner()" 
                  class="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                  title="Cerrar por esta sesión">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
    }
  `]
})
export class DailyTaskBannerComponent implements OnInit, OnDestroy {
  dailyTaskStatus: DailyTaskStatusDTO | null = null;
  showBanner = true;
  private refreshSubscription?: Subscription;
  private isDismissed = false;

  constructor(
    private wompiReportsService: WompiReportsService,
    private authorizationService: AuthorizationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Verificar si el usuario puede ver la funcionalidad de reportes
    if (this.authorizationService.hasPermission('INTEGRATIONS_WOMPI_READ')) {
      this.loadDailyTaskStatus();
      
      // Refrescar cada 5 minutos
      this.refreshSubscription = interval(5 * 60 * 1000).subscribe(() => {
        if (!this.isDismissed) {
          this.loadDailyTaskStatus();
        }
      });
    } else {
      this.showBanner = false;
    }
  }

  ngOnDestroy(): void {
    this.refreshSubscription?.unsubscribe();
  }

  loadDailyTaskStatus(): void {
    this.wompiReportsService.getDailyTaskStatus().subscribe({
      next: (status) => {
        this.dailyTaskStatus = status;
        // Solo mostrar si no está completo y no fue descartado
        this.showBanner = !status.isComplete && !this.isDismissed;
      },
      error: (error) => {
        console.error('Error cargando estado de tarea:', error);
        this.showBanner = false;
      }
    });
  }

  getStatusText(): string {
    if (!this.dailyTaskStatus) return '';
    
    const pending = [];
    if (!this.dailyTaskStatus.wompiUploaded) pending.push('Wompi');
    if (!this.dailyTaskStatus.plinkUploaded) pending.push('Plink');
    
    return `Pendiente: ${pending.join(' y ')}`;
  }

  dismissBanner(): void {
    this.isDismissed = true;
    this.showBanner = false;
  }
}

